import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Sun, Moon, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/favicon.png";

const LoginPage = () => {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(null); // null | 'email' | 'otp' | 'done'
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await login(loginEmail, loginPassword);
    if (result.success) {
      toast.success('Welcome back!');
    } else {
      toast.error(result.error);
    }
    setIsLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success('OTP sent to your email');
      setForgotMode('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: forgotEmail,
        otp,
        new_password: newPassword,
      });
      toast.success('Password reset successfully!');
      setForgotMode('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgot = () => {
    setForgotMode(null);
    setForgotEmail('');
    setOtp('');
    setNewPassword('');
  };

  const renderForgotPassword = () => {
    if (forgotMode === 'email') {
      return (
        <div className="space-y-4">
          <button onClick={resetForgot} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="forgot-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Sign In
          </button>
          <div className="text-center mb-2">
            <h3 className="text-lg font-semibold">Forgot Password</h3>
            <p className="text-sm text-muted-foreground">Enter your email to receive a reset OTP</p>
          </div>
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="Enter your email" required data-testid="forgot-email" />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading} data-testid="forgot-submit">
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : 'Send OTP'}
            </Button>
          </form>
        </div>
      );
    }
    if (forgotMode === 'otp') {
      return (
        <div className="space-y-4">
          <button onClick={() => setForgotMode('email')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center mb-2">
            <h3 className="text-lg font-semibold">Enter OTP</h3>
            <p className="text-sm text-muted-foreground">Check your email for the 6-digit code</p>
          </div>
          <form onSubmit={handleResetSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>OTP Code</Label>
              <Input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP" maxLength={6} required className="text-center text-xl tracking-[0.5em] font-mono" data-testid="reset-otp" />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" required minLength={6} data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading} data-testid="reset-submit">
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Resetting...</> : 'Reset Password'}
            </Button>
          </form>
        </div>
      );
    }
    if (forgotMode === 'done') {
      return (
        <div className="space-y-4 text-center py-4">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h3 className="text-lg font-semibold">Password Reset Successful</h3>
          <p className="text-sm text-muted-foreground">You can now sign in with your new password.</p>
          <Button onClick={resetForgot} className="w-full" data-testid="back-to-login-btn">Back to Sign In</Button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#05050A] via-[#111116] to-[#1a1a2e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B5CF6]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-4 mb-8">
            <img src={LOGO_URL} alt="Wisewit" className="w-16 h-16 rounded-xl object-cover shadow-xl" />
            <div>
              <h1 className="text-3xl font-bold">Wisewit Solutions</h1>
              <p className="text-white/60">Pvt Ltd</p>
            </div>
          </div>
          <h2 className="text-4xl font-bold mb-4 leading-tight">Human Resource<br />Management System</h2>
          <p className="text-lg text-white/70 mb-8 max-w-md">Streamline your HR operations with our AI-powered platform designed for modern AI startups.</p>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {['Employee Management', 'AI HR Assistant', 'Skills Tracking', 'OKRs & Goals', 'Payroll & Compliance', 'Research Tracking'].map((feature) => (
              <div key={feature} className="flex items-center gap-2 text-sm text-white/80">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Wisewit" className="w-12 h-12 rounded-lg object-cover" />
            <div><h1 className="text-xl font-bold">Wisewit HRMS</h1></div>
          </div>

          <div className="flex justify-end mb-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="login-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          <Card className="border-border/50">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Welcome</CardTitle>
              <CardDescription>Sign in to your account or create a new one</CardDescription>
            </CardHeader>
            <CardContent>
              {forgotMode ? (
                renderForgotPassword()
              ) : (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" type="email" placeholder="Enter your email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required data-testid="login-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input id="login-password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setForgotMode('email')} className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit">
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground pt-2 border-t">
                    Accounts are created by your admin. If you don't have one, contact HR.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By continuing, you agree to Wisewit's Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
